import Layout from "../../components/Layout/Layout";
import { useState, useEffect } from "react";
import { HollowDotsSpinner } from "react-epic-spinners";
import BackButton from "../../components/BackButton";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import ThirdPartyServices from "../../services/ThirdParty";
import fire from "../../utils/Toastr";

export default function EditOrganization() {
  const { organizationId } = useParams();
  const location = useLocation();
  const orgData = location.state?.organization;

  const [formData, setFormData] = useState({
    applicationName: "",
    companyName: "",
    companyUniqueIdentifier: "",
    address: "",
    accountNumber: "",
    tenantid: "",
    organizationId: organizationId || "",
  });

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchApplications();

    const loadFromStateOrApi = async () => {
      if (orgData) {
        setFormData(prev => ({
          ...prev,
          applicationName: orgData.applicationName || "",
          companyName: orgData.companyName || "",
          companyUniqueIdentifier: orgData.companyUniqueIdentifier || "",
          address: orgData.address || "",
          accountNumber: orgData.accountNumber || "",
        }));
        if (orgData.applicationName) {
          setPendingAppName(orgData.applicationName);
        }
        setPageLoading(false);
      } else {
        const searchParam = searchParams.get("search");
        if (searchParam) {
          await fetchOrganizationBySearch(searchParam);
        } else {
          setPageLoading(false);
        }
      }
    };

    loadFromStateOrApi();
  }, []);

  const fetchOrganizationBySearch = async (searchParam) => {
    try {
      const response = await ThirdPartyServices.fetchOrganization(searchParam);
      if (response.status && response.data?.organizationDetails) {
        const org = response.data.organizationDetails;
        setFormData(prev => ({
          ...prev,
          applicationName: org.applicationName || "",
          companyName: org.companyName || "",
          companyUniqueIdentifier: org.companyUniqueIdentifier || "",
          address: org.address || "",
          accountNumber: org.accountNumber || "",
        }));
        if (org.applicationName) {
          setPendingAppName(org.applicationName);
        }
      }
    } catch (err) {
      console.error("Error fetching organization:", err);
    } finally {
      setPageLoading(false);
    }
  };

  const [pendingAppName, setPendingAppName] = useState("");

  useEffect(() => {
    if (applications.length > 0 && pendingAppName) {
      const match = applications.find(
        app => app.applicationName === pendingAppName
      );
      if (match) {
        setFormData(prev => ({
          ...prev,
          tenantid: match.tenantId || "",
        }));
      }
      setPendingAppName("");
    }
  }, [applications, pendingAppName]);

  const fetchApplications = async () => {
    setApplicationsLoading(true);
    try {
      const response = await ThirdPartyServices.listApplications(1, 100);
      if (response.status && response.data?.result) {
        setApplications(response.data.result);
      }
    } catch (err) {
      console.error("Error fetching applications:", err);
    } finally {
      setApplicationsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleApplicationSelect = (e) => {
    const appId = e.target.value;
    const app = applications.find(a => a.applicationId === appId);
    if (app) {
      setFormData(prev => ({
        ...prev,
        applicationName: app.applicationName,
        tenantid: app.tenantId
      }));
      if (validationErrors.applicationName) {
        setValidationErrors(prev => ({ ...prev, applicationName: "" }));
      }
    }
  };

  const selectedAppId = applications.find(
    a => a.applicationName === formData.applicationName
  )?.applicationId || "";

  const validateForm = () => {
    let errors = {};
    const { applicationName, companyName, companyUniqueIdentifier, address } = formData;

    if (!applicationName.trim()) errors.applicationName = "Application Name is required";
    if (!companyName.trim()) errors.companyName = "Company Name is required";
    if (!companyUniqueIdentifier.trim()) errors.companyUniqueIdentifier = "Company Unique Identifier is required";
    if (!address.trim()) errors.address = "Address is required";

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      fire("error", "Please fix the validation errors");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await ThirdPartyServices.updateOrganization({
        applicationName: formData.applicationName.trim(),
        tenantid: formData.tenantid.trim(),
        organizationId: formData.organizationId.trim(),
        companyUniqueIdentifier: formData.companyUniqueIdentifier.trim(),
        companyName: formData.companyName.trim(),
        address: formData.address.trim(),
        accountNumber: formData.accountNumber.trim(),
      });

      if (response.status) {
        fire("success", "Organization updated successfully!");
        setTimeout(() => {
          navigate("/thirdparty/organizations");
        }, 1500);
      } else {
        setError(response.message || "Failed to update organization");
        fire("error", response.message || "Failed to update organization");
      }
    } catch (err) {
      setError("An error occurred while updating the organization");
      fire("error", "An error occurred while updating the organization");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <Layout title="Edit Organization" pageTag={"thirdparty"} current={"/thirdparty/organizations"}>
        <div className="flex justify-center py-20">
          <HollowDotsSpinner color="#4A90E2" size={40} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title="Edit Third-Party Organization"
      pageTag={"thirdparty"}
      current={"/thirdparty/organizations"}
    >
      <main>
        <div className="max-w-7xl mx-auto">
          <BackButton />
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="mt-8 flex flex-col">
              <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                  <div className="overflow-hidden ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white mt-5">
                    <div className="p-6">
                      <h2 className="text-lg font-medium text-gray-900 mb-6">Edit Organization</h2>

                      <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="applicationSelect" className="block text-sm font-medium text-gray-700">
                              Application *
                            </label>
                            <div className="mt-1 flex rounded-md shadow-sm">
                              <select
                                id="applicationSelect"
                                value={selectedAppId}
                                onChange={handleApplicationSelect}
                                disabled={applicationsLoading}
                                className={`block w-full rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm ${
                                  validationErrors.applicationName
                                    ? 'border-red-300'
                                    : 'border-gray-300'
                                } ${applicationsLoading ? 'opacity-50' : ''}`}
                              >
                                <option value="">-- Select an application --</option>
                                {applications.map(app => (
                                  <option key={app.applicationId} value={app.applicationId}>
                                    {app.applicationName}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {validationErrors.applicationName && (
                              <p className="mt-1 text-sm text-red-600">{validationErrors.applicationName}</p>
                            )}
                          </div>

                          <div>
                            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                              Company Name *
                            </label>
                            <input
                              type="text"
                              name="companyName"
                              id="companyName"
                              value={formData.companyName}
                              onChange={handleChange}
                              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm ${
                                validationErrors.companyName
                                  ? 'border-red-300'
                                  : 'border-gray-300'
                              }`}
                              placeholder="Enter company name"
                            />
                            {validationErrors.companyName && (
                              <p className="mt-1 text-sm text-red-600">{validationErrors.companyName}</p>
                            )}
                          </div>

                          <div>
                            <label htmlFor="companyUniqueIdentifier" className="block text-sm font-medium text-gray-700">
                              Company Unique Identifier *
                            </label>
                            <input
                              type="text"
                              name="companyUniqueIdentifier"
                              id="companyUniqueIdentifier"
                              value={formData.companyUniqueIdentifier}
                              onChange={handleChange}
                              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm ${
                                validationErrors.companyUniqueIdentifier
                                  ? 'border-red-300'
                                  : 'border-gray-300'
                              }`}
                              placeholder="Enter unique identifier"
                            />
                            {validationErrors.companyUniqueIdentifier && (
                              <p className="mt-1 text-sm text-red-600">{validationErrors.companyUniqueIdentifier}</p>
                            )}
                          </div>

                          <div>
                            <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700">
                              Account Number
                            </label>
                            <input
                              type="text"
                              name="accountNumber"
                              id="accountNumber"
                              value={formData.accountNumber}
                              onChange={handleChange}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                              placeholder="Enter account number"
                            />
                          </div>

                          <div className="sm:col-span-2">
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                              Address *
                            </label>
                            <textarea
                              name="address"
                              id="address"
                              rows={3}
                              value={formData.address}
                              onChange={handleChange}
                              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm ${
                                validationErrors.address
                                  ? 'border-red-300'
                                  : 'border-gray-300'
                              }`}
                              placeholder="Enter company address"
                            />
                            {validationErrors.address && (
                              <p className="mt-1 text-sm text-red-600">{validationErrors.address}</p>
                            )}
                          </div>
                        </div>

                        {error && (
                          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-600">{error}</p>
                          </div>
                        )}

                        <div className="mt-8 flex justify-end space-x-3">
                          <button
                            type="button"
                            onClick={() => navigate("/thirdparty/organizations")}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={loading}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                          >
                            {loading ? (
                              <>
                                <HollowDotsSpinner color="#ffffff" size={20} className="mr-2" />
                                Updating...
                              </>
                            ) : (
                              'Update Organization'
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
}
